// PaymentPlanCard — the plain-language plan summary for checkout Step 1.
//
// Replaces the dense BillingStateCard on the "Today" tab (that card still runs
// on the Treatment tab). Shows only what a front-desk person needs: how they
// pay in plain words, three numbers, and a Change link (before the plan locks).
// The heavy controls (change model / adjust bill / close plan) live behind the
// Change affordance in Step 2 / the Treatment tab.

import { ChevronRight } from "lucide-react";

const PRESET_WORDS = {
  "50_50": "50% now, 50% at the final visit",
  per_phase: "Split evenly across visits",
  custom: "Flexible",
  full: "Pay in full",
  plan_level: "Pay in full",
};

function peso(centavos) {
  const n = Math.round(Number(centavos || 0) / 100);
  return "₱" + n.toLocaleString();
}

export default function PaymentPlanCard({ state, onChangePlan, cardTutorialId, changeTutorialId, embedded = false, noPad = false, coverageNote = null, title = "Treatment plan billing" }) {
  if (!state) return null;
  const locked = Boolean(state.locked);
  const words = PRESET_WORDS[state.payment_preset] || "Not set yet";

  return (
    <div
      data-testid="payment-plan-card"
      data-tutorial-id={cardTutorialId}
      // When embedded, drop the card chrome so this reads as the billing
      // region of the shared plan+billing card rather than a separate card.
      className={embedded ? (noPad ? "h-full" : "p-4 h-full") : "bg-white border border-slate-200 rounded-xl p-4"}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-medium text-slate-800">{title}</span>
        <span
          data-testid="plan-status"
          className={
            "ml-auto text-[11px] px-2.5 py-0.5 rounded-full border " +
            (locked
              ? "bg-slate-100 text-slate-600 border-slate-200"
              : "bg-amber-50 text-amber-700 border-amber-200")
          }
        >
          {locked ? "Locked" : "Not started"}
        </span>
      </div>

      {coverageNote ? (
        <div className="-mt-2 mb-3 text-[11px] text-slate-400">{coverageNote}</div>
      ) : null}

      <div className="mb-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400 mb-1">Billing plan</div>
        {!locked && onChangePlan ? (
          <button
            type="button"
            data-testid="plan-change"
            data-tutorial-id={changeTutorialId}
            onClick={onChangePlan}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white hover:border-sky-400 hover:bg-sky-50 transition-colors text-left"
          >
            <span className="text-sm font-medium text-slate-800">{words}</span>
            <span className="text-xs font-medium text-sky-700 inline-flex items-center gap-0.5 shrink-0">
              Change <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </button>
        ) : (
          <div className="px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm font-medium text-slate-800">
            {words}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        <div className="bg-slate-50 rounded-lg px-3 py-2">
          <div className="text-[11px] text-slate-400">Plan total</div>
          <div data-testid="plan-total" className="text-sm font-medium text-slate-800">{peso(state.total_centavos)}</div>
        </div>
        <div className="bg-slate-50 rounded-lg px-3 py-2">
          <div className="text-[11px] text-slate-400">Paid so far</div>
          <div data-testid="plan-paid" className="text-sm font-medium text-slate-800">{peso(state.paid_centavos)}</div>
        </div>
        <div className="bg-slate-50 rounded-lg px-3 py-2">
          <div className="text-[11px] text-slate-400">Remaining</div>
          <div data-testid="plan-remaining" className="text-sm font-medium text-slate-800">{peso(state.remaining_centavos)}</div>
        </div>
      </div>
    </div>
  );
}
